/**
 * Cliente HTTP único de la app. Responsabilidades:
 *  - adjuntar el access token,
 *  - refrescar de forma TRANSPARENTE ante 401 (single-flight: un solo refresh
 *    concurrente aunque varias peticiones fallen a la vez),
 *  - normalizar los errores de dominio de la API a `ApiError`.
 * No conoce React: el AuthContext registra un callback `onUnauthorized` para
 * cerrar sesión cuando el refresh también falla.
 */
import type { ApiErrorBody, Tokens } from "./types.js";
import { clearSession, loadTokens, saveTokens } from "./tokenStore.js";

const BASE = import.meta.env.VITE_API_BASE ?? "";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let onUnauthorized: (() => void) | null = null;
export function setOnUnauthorized(cb: (() => void) | null): void {
  onUnauthorized = cb;
}

// ---- refresh single-flight ------------------------------------------------
let refreshing: Promise<Tokens | null> | null = null;

async function refrescar(): Promise<Tokens | null> {
  const tokens = loadTokens();
  if (!tokens) return null;
  const res = await fetch(`${BASE}/v1/identity/auth/refresh`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ refreshToken: tokens.refreshToken }),
  });
  if (!res.ok) return null;
  const data = (await res.json()) as { tokens: Tokens };
  saveTokens(data.tokens);
  return data.tokens;
}

function refrescarUnaVez(): Promise<Tokens | null> {
  if (!refreshing) {
    refreshing = refrescar().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

export interface RequestOpts {
  body?: unknown;
  /** Cuerpo multipart (subida de archivos). El navegador fija el content-type
   *  con boundary; por eso NO lo seteamos a mano. Excluyente con `body`. */
  form?: FormData;
  /** Enviar el access token (default true). Los endpoints de auth van sin. */
  auth?: boolean;
  query?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOpts["query"]): string {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
  }
  return url.pathname + url.search;
}

async function doFetch(
  method: string,
  url: string,
  opts: RequestOpts,
  accessToken: string | null,
): Promise<Response> {
  const headers: Record<string, string> = {};
  // multipart: el navegador pone el content-type con boundary; JSON: lo ponemos.
  if (opts.form === undefined && opts.body !== undefined) headers["content-type"] = "application/json";
  if (opts.auth !== false && accessToken) headers.authorization = `Bearer ${accessToken}`;
  const body =
    opts.form !== undefined
      ? opts.form
      : opts.body !== undefined
        ? JSON.stringify(opts.body)
        : undefined;
  return fetch(url, {
    method,
    headers,
    ...(body !== undefined ? { body } : {}),
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
}

async function parseError(res: Response): Promise<ApiError> {
  let code = "desconocido";
  let message = res.statusText || "error";
  try {
    const body = (await res.json()) as Partial<ApiErrorBody>;
    if (body.error) {
      code = body.error.code;
      message = body.error.message;
    }
  } catch {
    /* respuesta sin cuerpo JSON */
  }
  return new ApiError(res.status, code, message);
}

async function request<T>(method: string, path: string, opts: RequestOpts = {}): Promise<T> {
  const url = buildUrl(path, opts.query);
  const useAuth = opts.auth !== false;
  let res = await doFetch(method, url, opts, useAuth ? (loadTokens()?.accessToken ?? null) : null);

  // El access token dura ~15 min; en una batería larga expira a mitad y los
  // PATCH /items fallaban 401 en silencio (ítems perdidos). Ante 401 hacemos UN
  // refresh (single-flight) y reintentamos el request UNA sola vez.
  if (res.status === 401 && useAuth) {
    if (!loadTokens()?.refreshToken) {
      // No hay sesión que refrescar: es un deslogueo real → a /login.
      clearSession();
      onUnauthorized?.();
    } else {
      const nuevos = await refrescarUnaVez();
      if (nuevos) res = await doFetch(method, url, opts, nuevos.accessToken);
      if (!nuevos || res.status === 401) {
        // El refresh (o el reintento) falló: la sesión expiró en pleno uso.
        // NO deslogueamos en silencio (eso desmontaba la batería y perdía el
        // ítem en curso): lanzamos un error VISIBLE y distinguible
        // (`sesion_expirada`) para que la capa de arriba —el reproductor de la
        // batería— PAUSE y lo muestre, sin redirigir a login ni tirar lo ya
        // administrado. El usuario reinicia sesión y retoma la batería en curso.
        throw new ApiError(
          401,
          "sesion_expirada",
          "Tu sesión expiró. Vuelve a iniciar sesión para continuar.",
        );
      }
    }
  }

  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(path: string, opts?: RequestOpts) => request<T>("GET", path, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>("POST", path, { ...opts, ...(body !== undefined ? { body } : {}) }),
  put: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>("PUT", path, { ...opts, ...(body !== undefined ? { body } : {}) }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>("PATCH", path, { ...opts, ...(body !== undefined ? { body } : {}) }),
  del: <T>(path: string, opts?: RequestOpts) => request<T>("DELETE", path, opts),
  /** POST multipart (subida de archivo). El navegador fija el content-type. */
  postForm: <T>(path: string, form: FormData, opts?: RequestOpts) =>
    request<T>("POST", path, { ...opts, form }),
};

/** Sube un binario a una URL firmada (PUT directo a storage, fuera de /v1).
 *  `headers` son las cabeceras FIRMADAS que devuelve el API (hoy solo
 *  content-type; SSE deshabilitado temporalmente — MinIO prod sin KMS):
 *  deben enviarse EXACTAS o el storage responde 400 (falta una cabecera que
 *  va en X-Amz-SignedHeaders). */
export async function putSignedUrl(
  url: string,
  blob: Blob,
  headers: Record<string, string>,
): Promise<void> {
  const res = await fetch(url, { method: "PUT", headers, body: blob });
  if (!res.ok) {
    // MinIO/S3 devuelven el error real en un cuerpo XML (<Code>…</Code>); se
    // incluye recortado para poder diagnosticar (SignatureDoesNotMatch, etc.).
    const cuerpo = await res.text().catch(() => "");
    const detalle = cuerpo ? `: ${cuerpo.replace(/\s+/g, " ").trim().slice(0, 300)}` : "";
    throw new ApiError(res.status, "storage", `no se pudo subir el archivo${detalle}`);
  }
}
